import { EXPORT_DOCUMENT_TITLE_KEYS } from './exportExcelNaming';
import { uiT } from './uiTranslate';

/** Visible leading order for qlty008/011/012/013/014: checkbox → edit icon (no measures.id). */
export const MEASURE_GROUP_LEADING_COLUMN_FIELDS = Object.freeze(['rowSelection', 'editor']);

/**
 * Technical row identity for grouped measure lists.
 * Visible business id remains `order_number`; this key is hidden from the grid.
 */
export function measureGroupKey(type, orderNumber) {
  return `measure-group:${String(type ?? '')}:${String(orderNumber ?? '')}`;
}

/**
 * Saved user/standard masks may still store checkbox → id → editor.
 * Pin only rowSelection + editor when present; leave remaining columns intact.
 * Never invent a visible measures.id group identity.
 */
export function pinMeasureGroupLeadingColumns(attributes) {
  if (!Array.isArray(attributes) || attributes.length === 0) return attributes;
  const leadingSet = new Set(MEASURE_GROUP_LEADING_COLUMN_FIELDS);
  const byField = new Map();
  const rest = [];
  for (const node of attributes) {
    const field = node && typeof node === 'object' ? node.field : null;
    if (typeof field === 'string' && leadingSet.has(field) && !byField.has(field)) {
      byField.set(field, node);
    } else {
      rest.push(node);
    }
  }
  const leading = MEASURE_GROUP_LEADING_COLUMN_FIELDS
    .map((field) => byField.get(field))
    .filter(Boolean);
  if (leading.length === 0) return attributes;
  return [...leading, ...rest];
}

export const MEASURE_GROUP_LIST_MASKS = Object.freeze({
  /** qlty008 Dokumentácia → qlty009 add / qlty010 detail */
  documentation: Object.freeze({
    pageListKey: 'Documentation',
    listMask: 'qlty008',
    measureType: 'DOKUMENTÁCIA', // RAW API type token
    familyPath: 'documentation',
    pagedUrl: '/api/measure/documentation/paged',
    printUrl: '/api/measure/documentation/export/print',
    xlsxUrl: '/api/measure/documentation/export/xlsx',
    addPage: 'DocumentationAdd',
    detailPage: 'DocumentationDetail',
    addMask: 'qlty009',
    detailMask: 'qlty010',
    addTabKey: 'Otk.AddDocumentation',
    editTabKey: 'Otk.DocumentationDetail',
    get addTabName() { return uiT('Otk.AddDocumentation'); },
    get editTabName() { return uiT('Otk.DocumentationDetail'); },
    get favoriteTitle() { return uiT('Otk.Documentation'); },
    notificationPage: 'Documentation',
    exportTitleKey: EXPORT_DOCUMENT_TITLE_KEYS.MEASURE_DOCUMENTATION,
    permissions: Object.freeze({
      add: 'add otk-documentation',
      edit: 'edit otk-documentation',
      delete: 'delete otk-documentation',
    }),
  }),
  /** qlty011 Merací protokol → shared qlty009/qlty010 forms */
  measurementProtocol: Object.freeze({
    pageListKey: 'MeasurementProtocol',
    listMask: 'qlty011',
    measureType: 'MERACÍ PROTOKOL', // RAW API type token
    familyPath: 'measurementProtocol',
    pagedUrl: '/api/measure/measurementProtocol/paged',
    printUrl: '/api/measure/measurementProtocol/export/print',
    xlsxUrl: '/api/measure/measurementProtocol/export/xlsx',
    addPage: 'DocumentationAdd',
    detailPage: 'DocumentationDetail',
    addMask: 'qlty009',
    detailMask: 'qlty010',
    addTabKey: 'MeasureGroupList.Tab.AddMeasurementProtocol',
    editTabKey: 'MeasureGroupList.Tab.MeasurementProtocolDetail',
    get addTabName() { return uiT('MeasureGroupList.Tab.AddMeasurementProtocol'); },
    get editTabName() { return uiT('MeasureGroupList.Tab.MeasurementProtocolDetail'); },
    get favoriteTitle() { return uiT('MeasurementProtocol'); },
    notificationPage: 'MeasurementProtocol',
    exportTitleKey: EXPORT_DOCUMENT_TITLE_KEYS.MEASURE_MEASUREMENT_PROTOCOL,
    permissions: Object.freeze({
      add: 'add otk-section1',
      edit: 'add otk-section2',
      delete: 'add otk-section3',
    }),
  }),
  /** qlty012 Sebakontrola */
  selfControl: Object.freeze({
    pageListKey: 'SelfControl',
    listMask: 'qlty012',
    measureType: 'SEBAKONTROLA', // RAW API type token
    familyPath: 'selfControl',
    pagedUrl: '/api/measure/selfControl/paged',
    printUrl: '/api/measure/selfControl/export/print',
    xlsxUrl: '/api/measure/selfControl/export/xlsx',
    addPage: 'DocumentationAdd',
    detailPage: 'DocumentationDetail',
    addMask: 'qlty009',
    detailMask: 'qlty010',
    addTabKey: 'MeasureGroupList.Tab.AddSelfControl',
    editTabKey: 'MeasureGroupList.Tab.SelfControlDetail',
    get addTabName() { return uiT('MeasureGroupList.Tab.AddSelfControl'); },
    get editTabName() { return uiT('MeasureGroupList.Tab.SelfControlDetail'); },
    get favoriteTitle() { return uiT('Sebakontrola'); },
    notificationPage: 'SelfControl',
    exportTitleKey: EXPORT_DOCUMENT_TITLE_KEYS.MEASURE_SELF_CONTROL,
    permissions: Object.freeze({
      add: 'add otk-section1',
      edit: 'add otk-section2',
      delete: 'add otk-section3',
    }),
  }),
  /** qlty013 NDT protokol */
  ndtProtocol: Object.freeze({
    pageListKey: 'NDTProtocol',
    listMask: 'qlty013',
    measureType: 'NDT PROTOKOL', // RAW API type token
    familyPath: 'NDTProtocol',
    pagedUrl: '/api/measure/NDTProtocol/paged',
    printUrl: '/api/measure/NDTProtocol/export/print',
    xlsxUrl: '/api/measure/NDTProtocol/export/xlsx',
    addPage: 'DocumentationAdd',
    detailPage: 'DocumentationDetail',
    addMask: 'qlty009',
    detailMask: 'qlty010',
    addTabKey: 'MeasureGroupList.Tab.AddNdtProtocol',
    editTabKey: 'MeasureGroupList.Tab.NdtProtocolDetail',
    get addTabName() { return uiT('MeasureGroupList.Tab.AddNdtProtocol'); },
    get editTabName() { return uiT('MeasureGroupList.Tab.NdtProtocolDetail'); },
    get favoriteTitle() { return uiT('NDTProtocol'); },
    notificationPage: 'NDTProtocol',
    exportTitleKey: EXPORT_DOCUMENT_TITLE_KEYS.MEASURE_NDT_PROTOCOL,
    permissions: Object.freeze({
      add: 'add otk-section1',
      edit: 'add otk-section2',
      delete: 'add otk-section3',
    }),
  }),
  /** qlty014 Žíhací protokol */
  annealingProtocol: Object.freeze({
    pageListKey: 'AnnealingProtocol',
    listMask: 'qlty014',
    measureType: 'ŽÍHACÍ PROTOKOL', // RAW API type token
    familyPath: 'annealingProtocol',
    pagedUrl: '/api/measure/annealingProtocol/paged',
    printUrl: '/api/measure/annealingProtocol/export/print',
    xlsxUrl: '/api/measure/annealingProtocol/export/xlsx',
    addPage: 'DocumentationAdd',
    detailPage: 'DocumentationDetail',
    addMask: 'qlty009',
    detailMask: 'qlty010',
    addTabKey: 'MeasureGroupList.Tab.AddAnnealingProtocol',
    editTabKey: 'Otk.AnnealingProtocolDetail',
    get addTabName() { return uiT('MeasureGroupList.Tab.AddAnnealingProtocol'); },
    get editTabName() { return uiT('Otk.AnnealingProtocolDetail'); },
    get favoriteTitle() { return uiT('AnnealingProtocol'); },
    notificationPage: 'AnnealingProtocol',
    exportTitleKey: EXPORT_DOCUMENT_TITLE_KEYS.MEASURE_ANNEALING_PROTOCOL,
    permissions: Object.freeze({
      add: 'add otk-section1',
      edit: 'add otk-section2',
      delete: 'add otk-section3',
    }),
  }),
});
